import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { Calendar as Cal, MapPin, Search, Lock, Tag, Download } from "lucide-react";
import { toast } from "sonner";
import PublicLinkButton from "@/components/PublicLinkButton";
import { useLang } from "@/context/LangContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;

export default function MemberEvents() {
  const { t } = useLang();
  const [events, setEvents] = useState([]);
  const [regs, setRegs] = useState([]);
  const [q, setQ] = useState("");
  const [when, setWhen] = useState("upcoming");
  const [audience, setAudience] = useState("all");
  const [pricing, setPricing] = useState("all");
  const [sort, setSort] = useState("date-asc");

  useEffect(() => {
    const [s, o] = sort.split("-");
    const params = { when, sort: s, order: o };
    if (q.trim()) params.q = q.trim();
    if (audience !== "all") params.audience = audience;
    if (pricing !== "all") params.pricing = pricing;
    const tmo = setTimeout(() => {
      api.get("/events", { params }).then((r) => setEvents(r.data)).catch(() => {});
    }, q ? 300 : 0);
    return () => clearTimeout(tmo);
  }, [q, when, audience, pricing, sort]);
  useEffect(() => { api.get("/my/event-registrations").then((r) => setRegs(r.data)).catch(() => {}); }, []);

  const isRegistered = (id) => regs.find((r) => r.event_id === id && r.status !== "cancelled");
  const reg = async (e) => {
    // If the event has custom tickets defined, the user MUST pick one — redirect to detail page.
    try {
      await api.post("/event-registrations", { event_id: e.id, participation_type: "visitor" });
      toast.success("Inscription confirmée");
      api.get("/my/event-registrations").then((r) => setRegs(r.data));
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de l'inscription";
      toast.error(msg);
    }
  };

  return (
    <PortalLayout sidebar="member">
      <h1 className="text-3xl font-bold text-navy md:text-4xl">{t("portal_memberevents_01")}</h1>
      <p className="mt-1 text-neutral-600">{t("portal_memberevents_02")}</p>

      <div className="mt-6 grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("portal_memberevents_90")} className="w-full rounded-full border border-neutral-200 bg-white pl-10 pr-4 py-2 text-sm focus:border-wine-400 focus:outline-none" data-testid="member-events-search" />
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={when} onChange={(e) => setWhen(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="member-events-when">
            <option value="upcoming">{t("portal_memberevents_03")}</option>
            <option value="past">{t("portal_memberevents_04")}</option>
          </select>
          <select value={audience} onChange={(e) => setAudience(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="member-events-audience">
            <option value="all">{t("portal_memberevents_05")}</option>
            <option value="public">🌐 Public</option>
            <option value="members_only">🔒 Membres</option>
          </select>
          <select value={pricing} onChange={(e) => setPricing(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="member-events-pricing">
            <option value="all">Tarif</option>
            <option value="free">Gratuit</option>
            <option value="paid">Payant</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="member-events-sort">
            <option value="date-asc">{t("portal_memberevents_06")}</option>
            <option value="date-desc">{t("portal_memberevents_07")}</option>
            <option value="title-asc">Titre A → Z</option>
            <option value="title-desc">Titre Z → A</option>
            <option value="created_at-desc">{t("portal_memberevents_08")}</option>
            <option value="created_at-asc">{t("portal_memberevents_09")}</option>
          </select>
        </div>
      </div>

      <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {events.map((e) => {
          const r = isRegistered(e.id);
          const isPaid = e.pricing?.is_paid || (e.tickets || []).some((t) => (t.price || 0) > 0);
          return (
            <div key={e.id} className="overflow-hidden rounded-2xl border border-neutral-100 bg-white" data-testid={`portal-event-${e.id}`}>
              <Link to={r ? `/portal/event/${e.slug}/overview` : `/events/${e.slug}`} className="relative block">
                <img src={resolveUrl(e.image)} alt={e.title} className="aspect-[16/9] w-full object-cover" />
                <div className="absolute top-3 right-3 flex flex-col gap-1.5">
                  {e.audience === "members_only" && <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white shadow"><Lock className="h-2.5 w-2.5" />Membres</span>}
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase shadow ${isPaid ? "bg-wine-600 text-white" : "bg-emerald-500 text-white"}`}><Tag className="h-2.5 w-2.5" />{isPaid ? "Payant" : "Gratuit"}</span>
                </div>
              </Link>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-serif text-lg text-neutral-900">{e.title}</h3>
                  <PublicLinkButton slug={e.slug} type="event" compact />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-neutral-500">
                  <span className="inline-flex items-center gap-1"><Cal className="h-3 w-3" />{new Date(e.date).toLocaleDateString("fr", { day: "numeric", month: "short", year: "numeric" })}</span>
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{e.location}</span>
                  <a
                    href={`${BACKEND}/api/events/${e.id}/catalog.pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto inline-flex items-center gap-1 rounded-full border border-wine-200 bg-white px-2 py-0.5 text-[10px] font-bold text-wine-700 hover:bg-wine-50"
                    data-testid={`member-event-catalog-${e.id}`}
                  >
                    <Download className="h-3 w-3" />Catalogue
                  </a>
                </div>
                {r ? (
                  <Link to={`/portal/event/${e.slug}/overview`} className="mt-4 block w-full rounded-full bg-navy px-4 py-2 text-center text-xs font-semibold text-white hover:bg-wine-700" data-testid={`portal-event-details-${e.id}`}>
                    Afficher les détails →
                  </Link>
                ) : (
                  (e.tickets || []).length > 0 ? (
                    <Link to={`/events/${e.slug}`} className="mt-4 block w-full rounded-full bg-wine-500 px-4 py-2 text-center text-xs font-semibold text-white hover:bg-wine-600" data-testid={`portal-event-learn-more-${e.id}`}>
                      En savoir +
                    </Link>
                  ) : (
                    <button onClick={() => reg(e)} className="mt-4 w-full rounded-full bg-wine-500 px-4 py-2 text-xs font-semibold text-white hover:bg-wine-600" data-testid={`portal-event-register-${e.id}`}>
                      Je participe
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })}
        {events.length === 0 && <div className="col-span-full rounded-2xl border-2 border-dashed border-neutral-200 bg-neutral-50/50 px-6 py-16 text-center text-sm text-neutral-500" data-testid="member-events-empty">Aucun événement{q ? ` pour "${q}"` : ""}.</div>}
      </div>
    </PortalLayout>
  );
}
